///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

import {
	ContinuousGestureRecognizer,
	GestureState,
	type GestureEventData,
	type GestureRecognizerConfig,
	type TouchPoint
} from "./GestureRecognizer"

export interface ZoomGestureEventData extends GestureEventData {
	/** Current scale factor relative to start (1.0 = no change) */
	scale: number
	/** Change in scale since last event */
	scaleChange: number
	/** Velocity of scale change (scale units per second) */
	velocity: number
	/** Center point of the zoom (finger midpoint or mouse position) */
	centerX: number
	centerY: number
	/** Distance between the two fingers (0 for wheel zoom) */
	fingerDistance: number
	/** Whether this zoom came from a wheel event */
	isWheelZoom: boolean
	/** Original wheel delta Y (only set for wheel zoom) */
	wheelDeltaY?: number
	/** Whether this is a trackpad pinch-to-zoom (ctrlKey on wheel) */
	isTrackpadPinch?: boolean
	/** Whether ctrl key was held during wheel event */
	ctrlKey?: boolean
	/** Whether this gesture represents centroid translation (swipe) rather than scale change */
	isTranslation?: boolean
}

export interface ZoomGestureConfig extends GestureRecognizerConfig {
	/**
	 * Minimum scale change before zoom is recognized (default: 0.02)
	 * Lower values = more sensitive
	 */
	minimumScaleChange?: number
	/**
	 * Maximum rotation angle (radians) before this is considered rotation instead
	 * If rotation exceeds this while scale change is small, gesture fails
	 * Default: 0.15 (~8.5 degrees)
	 */
	rotationTolerance?: number
	/**
	 * Wheel sensitivity - how much scale change per wheel delta unit
	 * Default: 0.001
	 */
	wheelSensitivity?: number
	/**
	 * Time in ms after last wheel event before gesture ends (default: 150ms)
	 */
	wheelEndDelay?: number
	/**
	 * Recognize two-finger pinches (default true). Off when another
	 * recognizer owns two-finger touch (e.g. TwoFingerGestureRecognizer)
	 * and this one should only handle the wheel
	 */
	pinchEnabled?: boolean
}

/**
 * Recognizes zoom gestures from both pinch (two-finger) and wheel input
 *
 * This unified recognizer handles:
 * - **Pinch-to-zoom**: Two-finger gestures on touch devices
 * - **Wheel zoom**: Mouse wheel or trackpad scroll
 * - **Trackpad pinch**: ctrlKey + wheel (macOS trackpad pinch-to-zoom)
 *
 * For pinch gestures, it will fail if the gesture appears to be rotation
 * instead (use RotateGestureRecognizer for rotation detection).
 *
 * Zoom events are routed to the focused node via GestureFocusController.
 */
export class ZoomGestureRecognizer extends ContinuousGestureRecognizer<ZoomGestureEventData> {
	private minimumScaleChange: number
	private rotationTolerance: number
	private wheelSensitivity: number
	private wheelEndDelay: number
	private pinchEnabled: boolean
	// Pinch tracking state
	private startDistance: number = 0
	private lastDistance: number = 0
	private lastScale: number = 1
	public lastAngle: number = 0
	private startAngle: number = 0
	private lastUpdateTime: number = 0
	private velocity: number = 0
	private startCentroid?: { x: number, y: number }

	// Touch IDs for the two fingers
	public touch1Id?: number
	public touch2Id?: number

	// Wheel tracking state
	private isWheelGesture: boolean = false
	private wheelEndTimer?: ReturnType<typeof setTimeout>
	private wheelCumulativeScale: number = 1
	private lastWheelX: number = 0
	private lastWheelY: number = 0
	private lastWheelDeltaY: number = 0
	private isTrackpadPinch: boolean = false
	private lastCtrlKey: boolean = false

	constructor(config: ZoomGestureConfig = {}) {
		super({
			...config,
			minimumTouches: 2,
			maximumTouches: 2
		})
		this.minimumScaleChange = config.minimumScaleChange ?? 0.02
		this.rotationTolerance = config.rotationTolerance ?? 0.15
		this.wheelSensitivity = config.wheelSensitivity ?? 0.001
		this.wheelEndDelay = config.wheelEndDelay ?? 150
		this.pinchEnabled = config.pinchEnabled ?? true
	}

	public get gestureType(): string {
		return "zoom"
	}

	// ========== Wheel Event Handling ==========

	/**
	 * Handle a wheel event as a zoom gesture
	 * Call this from the gesture dispatcher when a wheel event occurs
	 *
	 * Wheel events bypass the gesture arena (no competition), so we
	 * auto-win and emit events directly.
	 *
	 * @param event The wheel event
	 * @param canvasX X position relative to canvas
	 * @param canvasY Y position relative to canvas
	 * @param physicalModifierHeld Whether a modifier key is physically held down.
	 *   macOS trackpad pinch-to-zoom synthesizes `ctrlKey` on the wheel event even
	 *   though no key is pressed, so it cannot be distinguished from a real
	 *   ctrl+scroll using the event alone. When a modifier is physically held we
	 *   treat the wheel as a normal scroll rather than a (200x) trackpad pinch.
	 */
	public handleWheelEvent(event: WheelEvent, canvasX: number, canvasY: number, physicalModifierHeld = false): void {
		if (!this._enabled) return

		// Clear any pending end timer
		this.clearWheelEndTimer()

		// A genuine macOS trackpad pinch forces only ctrlKey (no physical key).
		// Any physically-held modifier (tracked ctrl, or shift/alt/meta which are
		// never synthetic) means this is a modifier+scroll, not a pinch.
		const modifierHeld = physicalModifierHeld || event.shiftKey || event.altKey || event.metaKey

		// Track wheel state
		this.isWheelGesture = true
		this.lastWheelX = canvasX
		this.lastWheelY = canvasY
		this.lastWheelDeltaY = event.deltaY
		this.isTrackpadPinch = event.ctrlKey && !modifierHeld // macOS trackpad pinch-to-zoom
		this.lastCtrlKey = event.ctrlKey

		const now = performance.now()
		const dt = this.lastUpdateTime > 0 ? (now - this.lastUpdateTime) / 1000 : 0

		// Calculate scale change from wheel delta
		// Negative deltaY = scroll up = zoom in = scale > 1
		const scaleChange = -event.deltaY * this.wheelSensitivity
		const previousScale = this.wheelCumulativeScale
		this.wheelCumulativeScale *= (1 + scaleChange)

		// Calculate velocity
		if (dt > 0) {
			this.velocity = scaleChange / dt
		}

		this.lastUpdateTime = now
		this.lastScale = this.wheelCumulativeScale

		if (!this.isActive()) {
			// Begin the wheel zoom gesture
			const data = this.createWheelEventData(GestureState.Began)
			this.begin(data)
			// Wheel events bypass arena, so auto-win to emit the pending Began event
			this.didBecomeActive()
		} else {
			// Continue the gesture
			const data = this.createWheelEventData(GestureState.Changed)
			data.scaleChange = this.wheelCumulativeScale - previousScale
			this.change(data)
		}

		// Set timer to end gesture after delay
		this.wheelEndTimer = setTimeout(() => {
			this.endWheelGesture()
		}, this.wheelEndDelay)
	}

	/**
	 * Cancel any active wheel gesture
	 */
	public handleWheelCancel(): void {
		this.clearWheelEndTimer()
		if (this.isActive() && this.isWheelGesture) {
			this.cancel()
		}
		this.resetWheelState()
	}

	private endWheelGesture(): void {
		this.wheelEndTimer = undefined
		if (this.isActive() && this.isWheelGesture) {
			const data = this.createWheelEventData(GestureState.Ended)
			this.end(data)
		}
		this.resetWheelState()
	}

	private clearWheelEndTimer(): void {
		if (this.wheelEndTimer) {
			clearTimeout(this.wheelEndTimer)
			this.wheelEndTimer = undefined
		}
	}

	private resetWheelState(): void {
		this.isWheelGesture = false
		this.wheelCumulativeScale = 1
		this.lastWheelDeltaY = 0
		this.isTrackpadPinch = false
		this.lastCtrlKey = false
	}

	private createWheelEventData(state: GestureState): ZoomGestureEventData {
		return {
			type: "zoom",
			state,
			timestamp: performance.now(),
			x: this.lastWheelX,
			y: this.lastWheelY,
			ownerHits: this.getOwnerHits(),
			scale: this.wheelCumulativeScale,
			scaleChange: this.wheelCumulativeScale - 1,
			velocity: this.velocity,
			centerX: this.lastWheelX,
			centerY: this.lastWheelY,
			fingerDistance: 0,
			isWheelZoom: true,
			wheelDeltaY: this.lastWheelDeltaY,
			isTrackpadPinch: this.isTrackpadPinch,
			ctrlKey: this.lastCtrlKey
		}
	}

	// ========== Touch/Pinch Event Handling ==========

	public handleTouchStart(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// If a wheel gesture is active, cancel it
		if (this.isWheelGesture) {
			this.handleWheelCancel()
		}
		if (!this.pinchEnabled) return

		// Add touches to tracking
		for (const touch of touches) {
			this.touches.set(touch.id, touch)
		}

		// Need exactly 2 touches to begin
		if (this.touchCount !== 2) {
			return
		}

		this.initializePinchStart()
	}

	/**
	 * Capture the start state for a two-finger pinch. Called when the second
	 * finger lands, and again when the recognizer re-arms after a mid-session
	 * fail (fail() resets all tracking state).
	 */
	private initializePinchStart(): void {
		const touchArray = Array.from(this.touches.values())
		this.touch1Id = touchArray[0].id
		this.touch2Id = touchArray[1].id

		// Calculate initial distance and angle
		const { distance, angle } = this.calculateDistanceAndAngle()
		this.startDistance = distance
		this.lastDistance = distance
		this.startAngle = angle
		this.lastAngle = angle
		this.lastScale = 1
		this.lastUpdateTime = performance.now()
		this.velocity = 0
		this.startCentroid = this.getCentroid()
	}

	public handleTouchMove(touches: TouchPoint[]): void {
		if (!this._enabled || !this.pinchEnabled) return

		// Update tracked touches, adopting any the arena sends that we lost to
		// a mid-session fail/reset so the pinch can re-arm without lifting
		for (const touch of touches) {
			const existing = this.touches.get(touch.id)
			if (existing) {
				existing.x = touch.x
				existing.y = touch.y
			} else {
				this.touches.set(touch.id, touch)
			}
		}

		if (this.touchCount !== 2) return

		// Re-capture start state after a re-arm (fail() cleared it)
		if (this.startDistance === 0) {
			this.initializePinchStart()
			return
		}

		const now = performance.now()
		const dt = (now - this.lastUpdateTime) / 1000

		// Calculate current distance and angle
		const { distance, angle } = this.calculateDistanceAndAngle()

		// Calculate scale
		const scale = this.startDistance > 0 ? distance / this.startDistance : 1
		const scaleChange = scale - this.lastScale

		// Calculate rotation since start
		const totalRotation = Math.abs(this.normalizeAngle(angle - this.startAngle))

		// Discriminate only before the gesture begins - once active, the zoom
		// owns the touches and must keep emitting even if the geometry drifts
		const scaleChangeFromStart = Math.abs(scale - 1)
		if (!this.isActive() && totalRotation > this.rotationTolerance && scaleChangeFromStart < this.minimumScaleChange) {
			this.fail()
			return
		}

		// Common-mode (centroid) motion vs span change: a real pinch changes
		// the span at least twice as fast as the centroid moves (even with one
		// finger anchored), while a two-finger swipe moves the centroid far
		// faster than the span. When the centroid motion dominates, this is a
		// swipe — regardless of how much the span has incidentally drifted.
		const centroid = this.getCentroid()
		const cdx = this.startCentroid ? centroid.x - this.startCentroid.x : 0
		const cdy = this.startCentroid ? centroid.y - this.startCentroid.y : 0
		const centroidDist = Math.sqrt(cdx * cdx + cdy * cdy)
		const spanChangePx = Math.abs(distance - this.startDistance)
		const translationDominates = centroidDist > spanChangePx

		// Fail if centroid translation dominates (let RotateGestureRecognizer
		// handle swipes). The tolerance scales with finger span so wide pinches
		// aren't rejected by a few pixels of natural hand drift. A failed pinch
		// re-arms on the next move (see handleTouchMove), so this is a soft
		// yield rather than a dead end for the rest of the touch session.
		// Unlike before, this yield applies even when the span drift has
		// already exceeded minimumScaleChange — a swipe with uneven finger
		// speeds crosses that scale threshold almost immediately on a typical
		// mobile grip, and beginning the zoom here cancelled the two-finger
		// tilt swipe for the rest of the touch session.
		if (!this.isActive() && this.startCentroid && translationDominates) {
			const driftTolerance = Math.max(15, this.startDistance * 0.1)
			if (centroidDist > driftTolerance) {
				this.fail()
				return
			}
		}

		// Calculate velocity
		if (dt > 0) {
			this.velocity = scaleChange / dt
		}

		this.lastDistance = distance
		this.lastAngle = angle
		this.lastUpdateTime = now

		if (!this.isActive()) {
			// Require the span change to dominate the centroid motion before
			// beginning: scale alone crosses the threshold during a sloppy
			// two-finger swipe, and the zoom winning the arena here blocked
			// the tilt gesture (zoom is not allowSimultaneous).
			if (scaleChangeFromStart >= this.minimumScaleChange && !translationDominates) {
				this.lastScale = scale
				const data = this.createPinchEventData(GestureState.Began)
				this.begin(data)
			}
		} else {
			this.lastScale = scale
			const data = this.createPinchEventData(GestureState.Changed)
			this.change(data)
		}
	}

	public handleTouchEnd(touches: TouchPoint[]): void {
		if (!this._enabled || !this.pinchEnabled) return

		// Remove ended touches
		for (const touch of touches) {
			this.touches.delete(touch.id)
		}

		// End gesture if we drop below 2 touches
		if (this.touchCount < 2 && this.isActive()) {
			const data = this.createPinchEventData(GestureState.Ended)
			this.end(data)
		} else if (this.touchCount < 2 && !this.isActive() && !this.isTerminal()) {
			this.fail()
		}
	}

	public handleTouchCancel(): void {
		this.cancel()
	}

	private calculateDistanceAndAngle(): { distance: number; angle: number } {
		const touchArray = Array.from(this.touches.values())
		if (touchArray.length < 2) {
			return { distance: 0, angle: 0 }
		}

		const t1 = touchArray[0]
		const t2 = touchArray[1]

		const dx = t2.x - t1.x
		const dy = t2.y - t1.y

		return {
			distance: Math.sqrt(dx * dx + dy * dy),
			angle: Math.atan2(dy, dx)
		}
	}

	private normalizeAngle(angle: number): number {
		while (angle > Math.PI) angle -= 2 * Math.PI
		while (angle < -Math.PI) angle += 2 * Math.PI
		return angle
	}

	private createPinchEventData(state: GestureState): ZoomGestureEventData {
		const center = this.getCentroid()

		return {
			type: "zoom",
			state,
			timestamp: performance.now(),
			x: center.x,
			y: center.y,
			ownerHits: this.getOwnerHits(),
			scale: this.lastScale,
			scaleChange: this.lastScale - 1,
			velocity: this.velocity,
			centerX: center.x,
			centerY: center.y,
			fingerDistance: this.lastDistance,
			isWheelZoom: false
		}
	}

	protected createEventData(state: GestureState): ZoomGestureEventData {
		// Default to pinch event data
		return this.createPinchEventData(state)
	}

	override reset(): void {
		super.reset()
		// Reset pinch state
		this.startDistance = 0
		this.lastDistance = 0
		this.lastScale = 1
		this.lastAngle = 0
		this.startAngle = 0
		this.lastUpdateTime = 0
		this.velocity = 0
		this.touch1Id = undefined
		this.touch2Id = undefined
		this.startCentroid = undefined
		// Reset wheel state
		this.clearWheelEndTimer()
		this.resetWheelState()
	}
}
